import AppContext from 'App/AppContext';
import { IPDropdown, IPLink, IPSearchBox, IPSpinner, IPText, IPTextField } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import { globalStackTokensGapSmall } from 'globalStyles';
import ISOControl from 'models/isoControl';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetISOControlsForLang,
  apiGetISOControlsForStandard,
  apiGetISONorms,
  loadISOControlReference,
} from 'services/Api/isoControlService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';

interface IISOControlReferencePickerProps {
  initialId?: number;
  initialLabel?: string;
  onClose: () => void;
  onInsert: (id: number, label: string) => void;
}

/** The two fields the picker needs, so the catalog and the tenant cache can feed the same dropdown. */
interface IPickerNorm {
  isoNormId: number;
  name: string;
}

const ISOControlReferencePicker = (props: IISOControlReferencePickerProps) => {
  const app = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const [norms, setNorms] = useState<IPickerNorm[]>([]);
  const [normId, setNormId] = useState<number>();
  const [controls, setControls] = useState<ISOControl[]>([]);
  const [selected, setSelected] = useState<number>(props.initialId ?? 0);
  const [label, setLabel] = useState(props.initialLabel ?? '');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  // The link text is only replaced while it is still the generated caption; text the user selected or typed stays.
  const autoLabelRef = useRef('');
  const labelIsAuto = label.trim() === '' || label === autoLabelRef.current;
  const catalog = hasUserFeature(app, FeatureTypes.TenantISOAdmin);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        let available: IPickerNorm[];
        if (catalog) {
          const token = await app.getAccessToken(apiRequest.scopes);
          const all = await apiGetISONorms(true, token);
          available = all.map((norm) => ({ isoNormId: norm.isoNormId, name: norm.name }));
        } else {
          // The tenant cache already holds the active standards, names included, so neither the
          // catalog round trip nor the join against it is needed here.
          await app.globalDataCache.norms.getItems();
          available = app.globalDataCache.norms
            .getItemsISO()
            .filter((norm) => norm.active)
            .map((norm) => ({ isoNormId: norm.isoNormId as number, name: norm.name }));
        }

        // A reference whose clause no longer resolves must still leave the picker usable, so the
        // user can point it somewhere else.
        let current: ISOControl | undefined;
        if (props.initialId) {
          try {
            current = await loadISOControlReference(app, props.initialId);
          } catch {
            if (active) app.showNotification(t('translation:RichText.Reference.Unavailable'), true, false, true);
          }
        }
        if (!active) return;
        setNorms(available);
        if (current) {
          autoLabelRef.current = t('translation:RichText.Reference.Caption', {
            standard: available.find((norm) => norm.isoNormId === current?.isoNormId)?.name ?? '',
            code: current.code,
          });
        }
        // Only select a standard the dropdown actually lists, or the caption renders with a blank name.
        const currentNormId = available.some((norm) => norm.isoNormId === current?.isoNormId)
          ? current?.isoNormId
          : undefined;
        setNormId(currentNormId ?? (available.length === 1 ? available[0].isoNormId : undefined));
      } catch (error) {
        if (active) app.setError(error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!normId) return;
    let active = true;
    setLoading(true);
    setControls([]);
    const load = async () => {
      try {
        const token = await app.getAccessToken(apiRequest.scopes);
        const values = catalog
          ? await apiGetISOControlsForLang(normId, app.user.language.languageId, token, app.globalDataCache)
          : await apiGetISOControlsForStandard(normId, token, app.globalDataCache);
        if (active) setControls(values);
      } catch (error) {
        if (active) app.setError(error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normId]);

  const filter = search.trim().toLocaleLowerCase();
  const matchingControls = controls.filter((c) => `${c.code} ${c.name}`.toLocaleLowerCase().includes(filter));

  return (
    <GenericModal
      isOpen
      onClose={props.onClose}
      title={t('translation:RichText.Reference.Insert')}
      saveButtonText={t('translation:RichText.Reference.Insert')}
      keepOpenAfterSave
      saveDisabled={loading || !selected || !label.trim() || !controls.some((c) => c.isoControlId === selected)}
      onSave={() => props.onInsert(selected, label.trim())}
      maxWidth={640}
      width="90vw"
    >
      <IPStack tokens={globalStackTokensGapSmall}>
        <IPDropdown
          label={t('translation:RichText.Reference.Standard')}
          selectedKey={normId}
          options={norms.map((norm) => ({ key: norm.isoNormId, text: norm.name }))}
          onChange={(_, option) => {
            setNormId(option?.key as number);
            setSelected(0);
            if (labelIsAuto) setLabel('');
          }}
        />
        <IPSearchBox
          placeholder={t('translation:RichText.Reference.Search')}
          value={search}
          onChange={(_, value) => setSearch(value ?? '')}
        />
        {loading ? (
          <IPSpinner />
        ) : (
          <IPStack
            disableShrink
            styles={{ root: { maxHeight: 280, overflowY: 'auto' } }}
            tokens={globalStackTokensGapSmall}
          >
            {matchingControls.map((c) => (
              <IPLink
                key={c.isoControlId}
                styles={{
                  root: {
                    fontWeight: c.isoControlId === selected ? 600 : 400,
                    overflow: 'visible',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  },
                }}
                onClick={() => {
                  setSelected(c.isoControlId);
                  const auto = t('translation:RichText.Reference.Caption', {
                    standard: norms.find((n) => n.isoNormId === normId)?.name ?? '',
                    code: c.code,
                  });
                  if (labelIsAuto) setLabel(auto);
                  autoLabelRef.current = auto;
                }}
              >
                {t('translation:RichText.Reference.Option', { code: c.code, name: c.name })}
              </IPLink>
            ))}
            {matchingControls.length === 0 && <IPText>{t('translation:RichText.Reference.Empty')}</IPText>}
          </IPStack>
        )}
        <IPTextField
          label={t('translation:RichText.Reference.Label')}
          value={label}
          onChange={(_, value) => setLabel(value ?? '')}
        />
      </IPStack>
    </GenericModal>
  );
};

export default ISOControlReferencePicker;
